/**
 * Branded type definitions for all database IDs
 * This ensures type safety and prevents mixing different ID types
 */

// Generic distinct type definition (same pattern as MDI types)
type Distinct<T, DistinctName> = T & { __TYPE__: DistinctName };

// ============================================
// Internal Database IDs (UUIDs)
// ============================================

export type AccountID = Distinct<string, "AccountID">;
export type MemberID = Distinct<string, "MemberID">;
export type EpisodeID = Distinct<string, "EpisodeID">;
export type ShortLinkID = Distinct<string, "ShortLinkID">;
export type LinkEventID = Distinct<string, "LinkEventID">;
export type StatsigEventID = Distinct<string, "StatsigEventID">;

// ============================================
// External System IDs
// ============================================

// From Kinsa/Owlet systems
export type ExternalAccountID = Distinct<string, "ExternalAccountID">;
export type ExternalMemberID = Distinct<string, "ExternalMemberID">;

// Client-generated key for an on-device scheduled notification. Doubles as the
// upsert key: scheduling an existing id replaces the pending request.
export type LocalNotificationID = Distinct<string, "LocalNotificationID">;

// From payment provider (Stripe)
export type PaymentID = Distinct<string, "PaymentID">;
export type StripeCustomerID = Distinct<string, "StripeCustomerID">;
export type StripeSubscriptionID = Distinct<string, "StripeSubscriptionID">;
export type StripePromotionCodeID = Distinct<string, "StripePromotionCodeID">;

// From MDI (Medical provider network)
export type MDIPatientID = Distinct<string, "MDIPatientID">;
export type MDIVisitID = Distinct<string, "MDIVisitID">;
export type MDICaseID = Distinct<string, "MDICaseID">;
export type MDIClinicianID = Distinct<string, "MDIClinicianID">;

// From Blueberry (Pediatric provider network)
export type BlueberryChildID = Distinct<string, "BlueberryChildID">;
export type BlueberryVisitID = Distinct<string, "BlueberryVisitID">;
export type BlueberryProfileID = Distinct<string, "BlueberryProfileID">;

// Places: USPS ZIPs, Census FIPS codes (zero-padded), CDC health service areas
export type ZipCode = Distinct<string, "ZipCode">;
export type StateFIPS = Distinct<string, "StateFIPS">;
export type CountyFIPS = Distinct<string, "CountyFIPS">;
export type HsaID = Distinct<string, "HsaID">;

export const asBlueberryChildID = (id: string): BlueberryChildID =>
  id as BlueberryChildID;

export const asBlueberryVisitID = (id: string): BlueberryVisitID =>
  id as BlueberryVisitID;

export const asBlueberryProfileID = (id: string): BlueberryProfileID =>
  id as BlueberryProfileID;

// ============================================
// Helper functions for safe type conversion
// ============================================

// Internal IDs
export const asAccountID = (id: string): AccountID => id as AccountID;
export const asMemberID = (id: string): MemberID => id as MemberID;
export const asEpisodeID = (id: string): EpisodeID => id as EpisodeID;
export const asShortLinkID = (id: string): ShortLinkID => id as ShortLinkID;
export const asLinkEventID = (id: string): LinkEventID => id as LinkEventID;
export const asStatsigEventID = (id: string): StatsigEventID =>
  id as StatsigEventID;

// External IDs
export const asExternalAccountID = (id: string): ExternalAccountID =>
  id as ExternalAccountID;
export const asExternalMemberID = (id: string): ExternalMemberID =>
  id as ExternalMemberID;
export const asLocalNotificationID = (id: string): LocalNotificationID =>
  id as LocalNotificationID;
export const asPaymentID = (id: string): PaymentID => id as PaymentID;
export const asStripeCustomerID = (id: string): StripeCustomerID =>
  id as StripeCustomerID;
export const asStripeSubscriptionID = (id: string): StripeSubscriptionID =>
  id as StripeSubscriptionID;
export const asStripePromotionCodeID = (id: string): StripePromotionCodeID =>
  id as StripePromotionCodeID;
export const asMDIPatientID = (id: string): MDIPatientID => id as MDIPatientID;
export const asMDIVisitID = (id: string): MDIVisitID => id as MDIVisitID;
export const asMDICaseID = (id: string): MDICaseID => id as MDICaseID;
export const asMDIClinicianID = (id: string): MDIClinicianID =>
  id as MDIClinicianID;
export const asZipCode = (zip: string): ZipCode => zip as ZipCode;
export const asStateFIPS = (fips: string): StateFIPS => fips as StateFIPS;
export const asCountyFIPS = (fips: string): CountyFIPS => fips as CountyFIPS;
export const asHsaID = (id: string): HsaID => id as HsaID;

// ============================================
// Type guards for runtime validation
// ============================================

export const isValidUUID = (id: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

export const isAccountID = (id: unknown): id is AccountID => {
  return typeof id === "string" && isValidUUID(id);
};

export const isMemberID = (id: unknown): id is MemberID => {
  return typeof id === "string" && isValidUUID(id);
};

export const isEpisodeID = (id: unknown): id is EpisodeID => {
  return typeof id === "string" && isValidUUID(id);
};

export const isMDIPatientID = (id: unknown): id is MDIPatientID => {
  return typeof id === "string" && id.length > 0;
};

export const isMDIVisitID = (id: unknown): id is MDIVisitID => {
  return typeof id === "string" && id.length > 0;
};

// ============================================
// Cross-brand union types
// ============================================

/** The provider-network member ID stored on a Member record — varies by brand */
export type ProviderNetworkMemberID =
  | MDIPatientID
  | BlueberryProfileID
  | BlueberryChildID;

export const asProviderNetworkMemberID = (
  id: string
): ProviderNetworkMemberID => id as ProviderNetworkMemberID;

export const asProviderNetworkMemberIDOrNull = (
  id: string | null | undefined
): ProviderNetworkMemberID | null =>
  id ? asProviderNetworkMemberID(id) : null;

/**
 * Visit / case id in app routes (`/visit/...`) and provider messaging — MDI
 * case id or Blueberry visit external id.
 */
export type ProviderNetworkVisitID = MDICaseID | BlueberryVisitID;

export const asProviderNetworkVisitID = (
  id: string
): ProviderNetworkVisitID => id as ProviderNetworkVisitID;

// ============================================
// Nullable helper functions
// ============================================

export const asMemberIDOrNull = (
  id: string | null | undefined
): MemberID | null => (id ? asMemberID(id) : null);

export const asEpisodeIDOrNull = (
  id: string | null | undefined
): EpisodeID | null => (id ? asEpisodeID(id) : null);

export const asMDIPatientIDOrNull = (
  id: string | null | undefined
): MDIPatientID | null => (id ? asMDIPatientID(id) : null);

export const asMDIVisitIDOrNull = (
  id: string | null | undefined
): MDIVisitID | null => (id ? asMDIVisitID(id) : null);

export const asPaymentIDOrNull = (
  id: string | null | undefined
): PaymentID | null => (id ? asPaymentID(id) : null);

// ============================================
// Utility function to extract raw string value
// ============================================

export const extractID = <T extends string>(id: T): string => id;
